import tooltipPointerHTMLihm from '../../../templates/measureihm_pointer_tooltip';
import tooltipHTMLihm from '../../../templates/measureihm_tooltip';
import { getValue } from '../../../facade/js/i18n/language';

/**
 * @classdesc
 * Main constructor of the measureihm control.
 *
 * @constructor
 * @param {string} type - Type of the measureihm geometry
 * @extends {M.impl.Control}
 * @api stable
 */
export default class Measureihm extends M.impl.Control {
  constructor(type) {
    super();

    /**
     * Type of the measureihm geometry
     * @private
     * @type {string}
     */
    this.type_ = type;

    /**
     * Vector layer to draw the measures
     * @private
     * @type {ol.layer.Vector}
     */
    this.layer_ = this.createLayer_();

    /**
     * Map interaction
     * @private
     * @type {ol.interaction.Draw}
     */
    this.draw_ = this.createIteractionDraw_();

    /**
     * Overlay to show the help messages
     * @private
     * @type {ol.Overlay}
     */
    this.helpTooltip_ = null;

    /**
     * The measureihm tooltip element
     * @private
     * @type {ol.Overlay}
     */
    this.measureTooltip_ = null;

    /**
     * Facade of the map
     * @private
     * @type {M.Map}
     */
    this.facadeMap_ = null;

    /**
     * Currently drawn feature.
     * @private
     * @type {ol.Feature}
     */
    this.currentFeature_ = null;

    /**
     * Currently drawn feature coordinate.
     * @private
     * @type {ol.Coordinate}
     */
    this.tooltipCoord_ = null;

    /**
     * Currently drawn feature coordinate.
     * @private
     * @type {array<ol.Overlay>}
     */
    this.overlays_ = [];

    this.firstClick = true;
  }

  /**
   * This function adds the control to the specified map
   *
   * @public
   * @function
   * @param {M.Map} map - Map to add the plugin
   * @param {HTMLElement} element - template of this control
   * @api stable
   */
  addTo(map, element) {
    this.facadeMap_ = map;
    // adds layer
    map.getMapImpl().addLayer(this.layer_);
    // super addTo
    super.addTo(map, element);
    this.createHelpTooltip_();
    this.createMeasureTooltip_();
    this.createMeasureTooltipOld_();
    this.createMeasureTooltipPrevious_();
    this.createMeasureTooltipPprevious_();
    this.createMeasureTooltipLeft_();
  }

  /**
   * This function enables plugin pressed
   *
   * @public
   * @function
   * @api stable
   */
  activate() {
    this.invokeEscKey();
    this.createHelpTooltip_();
    this.facadeMap_.getMapImpl().on('pointermove', this.pointerMoveHandler_.bind(this));
    this.facadeMap_.getMapImpl().addInteraction(this.draw_);
    this.active = true;
    this.createMeasureTooltip_();
    this.createMeasureTooltipOld_();
    this.createMeasureTooltipPrevious_();
    this.createMeasureTooltipPprevious_();
    this.createMeasureTooltipLeft_();
    document.body.style.cursor = 'crosshair';
    document.addEventListener('keyup', this.checkEscKey.bind(this));
  }

  checkEscKey(evt) {
    if (evt.key === 'Escape') {
      document.querySelectorAll('.m-panel.m-panel-measurebarihm .m-panel-controls div.activated > button').forEach((elem) => {
        elem.click();
      });

      document.removeEventListener('keyup', this.checkEscKey);
    }
  }

  invokeEscKey() {
    try {
      document.dispatchEvent(new window.KeyboardEvent('keyup', {
        key: 'Escape',
        keyCode: 27,
        code: '',
        which: 69,
        shiftKey: false,
        ctrlKey: false,
        metaKey: false,
      }));
    } catch (err) {
      /* eslint-disable no-console */
      console.error(err);
    }
  }

  /**
   * This function dissable plugin
   *
   * @public
   * @function
   * @api stable
   */
  deactivate() {
    document.body.style.cursor = 'default';
    this.facadeMap_.getMapImpl().un('pointermove', this.pointerMoveHandler_.bind(this));
    this.facadeMap_.getMapImpl().removeInteraction(this.draw_);
    this.clear();
    if (!M.utils.isNullOrEmpty(this.helpTooltip_)) {
      this.facadeMap_.getMapImpl().removeOverlay(this.helpTooltip_);
    }
    if (!M.utils.isNullOrEmpty(this.measureTooltip_)) {
      this.facadeMap_.getMapImpl().removeOverlay(this.measureTooltip_);
    }
    this.active = false;
  }

  /**
   * This function create Vector layer to draw the measures
   *
   * @function
   * @private
   * @return {ol.layer.Vector} layer - Vector layer
   */
  createLayer_() {
    const layer = new ol.layer.Vector({
      source: new ol.source.Vector({}),
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: 'rgba(51, 124, 235, 0.2)',
        }),
        stroke: new ol.style.Stroke({
          color: '#337ceb',
          width: 2,
        }),
        image: new ol.style.Circle({
          radius: 7,
          fill: new ol.style.Fill({
            color: '#337ceb',
          }),
        }),
      }),
      zIndex: 9999999999999,
    });
    return layer;
  }

  /**
   * This function create interaction draw
   *
   * @private
   * @function
   * @return {ol.interaction.Draw} draw - Interaction draw
   */
  createIteractionDraw_() {
    const draw = new ol.interaction.Draw({
      source: this.layer_.getSource(),
      type: this.type_,
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: 'rgba(255, 255, 255, 0.5)',
        }),
        stroke: new ol.style.Stroke({
          color: '#337ceb',
          lineDash: [10, 10],
          width: 2,
        }),
        image: new ol.style.Circle({
          radius: 5,
          stroke: new ol.style.Stroke({
            color: '#337ceb',
          }),
          fill: new ol.style.Fill({
            color: 'rgba(255, 255, 255, 0.5)',
          }),
        }),
      }),
    });
    draw.on('drawstart', this.onDrawStart_.bind(this));
    draw.on('drawend', this.onDrawEnd_.bind(this));

    return draw;
  }

  /**
   * This function create tooltip with the help
   *
   * @private
   * @function
   * @return {Promise} Template tooltip
   */
  createHelpTooltip_() {
    const helpTooltipElement = M.template.compileSync(tooltipPointerHTMLihm, {
      jsonp: true,
      vars: {
        translations: getValue('text'),
      },
    });

    this.helpTooltip_ = new ol.Overlay({
      element: helpTooltipElement,
      offset: [15, 0],
      positioning: 'center-left',
    });
    this.facadeMap_.getMapImpl().addOverlay(this.helpTooltip_);
  }

  /**
   * This function create Measureihm tooltip
   *
   * @private
   * @function
   */
  createMeasureTooltip_() {
    const measureTooltipElement = M.template.compileSync(tooltipHTMLihm, {
      jsonp: true,
      vars: {
        translations: getValue('text'),
      },
    });

    if (!M.utils.isNullOrEmpty(this.measureTooltip_)) {
      this.overlays_.push(this.measureTooltip_);
    }
    this.measureTooltip_ = new ol.Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });
    this.facadeMap_.getMapImpl().addOverlay(this.measureTooltip_);
  }

  /**
   * This function create Measureihm tooltip old
   *
   * @private
   * @function
   */
  createMeasureTooltipOld_() {
    const measureTooltipElement = M.template.compileSync(tooltipHTMLihm, {
      jsonp: true,
      vars: {
        translations: getValue('text'),
      },
    });

    if (!M.utils.isNullOrEmpty(this.measureTooltipOld_)) {
      this.overlays_.push(this.measureTooltipOld_);
    }
    this.measureTooltipOld_ = new ol.Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });
    this.facadeMap_.getMapImpl().addOverlay(this.measureTooltipOld_);
  }

  /**
   * This function create Measureihm tooltip previous
   *
   * @private
   * @function
   */
  createMeasureTooltipPrevious_() {
    const measureTooltipElement = M.template.compileSync(tooltipHTMLihm, {
      jsonp: true,
      vars: {
        translations: getValue('text'),
      },
    });
    if (!M.utils.isNullOrEmpty(this.measureTooltipPrevious_)) {
      this.overlays_.push(this.measureTooltipPrevious_);
    }
    this.measureTooltipPrevious_ = new ol.Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });
    this.facadeMap_.getMapImpl().addOverlay(this.measureTooltipPrevious_);
  }

  /**
   * This
   function create Measureihm tooltip previous previous
   *
   * @private
   * @function
   */
  createMeasureTooltipPprevious_() {
    const measureTooltipElement = M.template.compileSync(tooltipHTMLihm, {
      jsonp: true,
      vars: {
        translations: getValue('text'),
      },
    });
    if (!M.utils.isNullOrEmpty(this.measureTooltipPprevious_)) {
      this.overlays_.push(this.measureTooltipPprevious_);
    }
    this.measureTooltipPprevious_ = new ol.Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });
    this.facadeMap_.getMapImpl().addOverlay(this.measureTooltipPprevious_);
  }

  /**
   * This
   function create Measureihm tooltip previous previous
   *
   * @private
   * @function
   */
  createMeasureTooltipLeft_() {
    const measureTooltipElement = M.template.compileSync(tooltipHTMLihm, {
      jsonp: true,
      vars: {
        translations: getValue('text'),
      },
    });
    if (!M.utils.isNullOrEmpty(this.measureTooltipLeft_)) {
      this.overlays_.push(this.measureTooltipLeft_);
    }
    this.measureTooltipLeft_ = new ol.Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });
    this.facadeMap_.getMapImpl().addOverlay(this.measureTooltipLeft_);
  }

  /**
   * This function create Measureihm tooltip previous
   *
   * @private
   * @function
   */
  createMeasureTooltipExtra_(coor, valor) {
    const measureTooltipElement = M.template.compileSync(tooltipHTMLihm, {
      jsonp: true,
      vars: {
        translations: getValue('text'),
      },
    });
    const measureTooltipExtra = new ol.Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
    });
    if (!M.utils.isNullOrEmpty(measureTooltipExtra)) {
      this.overlays_.push(measureTooltipExtra);
    }
    measureTooltipExtra.getElement().innerHTML = valor;
    measureTooltipExtra.setPosition(coor);
    this.facadeMap_.getMapImpl().addOverlay(measureTooltipExtra);
  }

  /**
   * This function allows end to draw
   *
   * @private
   * @function
   * @param {goog.events.Event} evt - Event draw end
   */
  onDrawEnd_(evt) {
    this.facadeMap_.un('click', window.fnClickMeasureBase);
    window.funcion = null;
    this.firstClick = true;
    this.currentFeature_.getGeometry().un('change', this.onGeometryChange_);


    // unset sketch
    this.currentFeature_ = null;
    this.measureTooltip_.getElement().classList.add('static');
    this.measureTooltip_.setOffset([0, -7]);
    this.valueRumboExtra_ = null;
    this.geomEvent = null;
    this.createMeasureTooltip_();
    this.createMeasureTooltipOld_();
    this.createMeasureTooltipPrevious_();
    this.createMeasureTooltipPprevious_();
    this.createMeasureTooltipLeft_();
  }

  /**
   * This function allows start to draw
   * @private
   * @function
   * @param {goog.events.Event} evt - Event draw start
   */
  onDrawStart_(evt) {
    const plugins = this.facadeMap_.getPlugins().filter((plugin) => {
      return plugin.name === 'measurebarihm';
    });
    const conf = plugins[0].getControls().filter((control) => {
      return (control.name === 'measureconfiguracionihm');
    })[0];
    const elemChecked = conf.getImpl().getChecked();
    this.clear();
    this.currentFeature_ = evt.feature;
    this.tooltipCoord_ = evt.coordinate;
    this.currentFeature_.getGeometry().on('change', this.onGeometryChange_.bind(this));
    if (elemChecked.includes('continuo')) {
      window.fnClickMeasureBase = () => {
        // aquí podemos ver el tema de las areas tramos
        // if (this.firstClick === false && this.name !== 'MeasureAreaihm') {
        if (this.firstClick === false) {
          setTimeout(this.dibujar_.bind(this), 300);
        } else {
          this.firstClick = false;
        }
      };
      this.facadeMap_.on('click', window.fnClickMeasureBase);
      conf.getImpl().setEventCheck(false);
    }
  }

  /**
   * This function draw overlay on click position
   * @private
   * @function
   * @param {goog.events.Event} evt - Event draw start
   */
  dibujar_() {
    if (this.geomEvent !== undefined ||
      this.geomEvent !== null) {
      let coordinates = this.geomEvent.getCoordinates();

      const plugins = this.facadeMap_.getPlugins().filter((plugin) => {
        return plugin.name === 'measurebarihm';
      });
      const conf = plugins[0].getControls().filter((control) => {
        return (control.name === 'measureconfiguracionihm');
      })[0];
      const elemChecked = conf.getImpl().getChecked();

      if (this.name === 'MeasureAreaihm') {
        coordinates = coordinates[0];
        // coordinates = coordinates[coordinates.length - 3];

        const coord1 = coordinates[coordinates.length - 3];
        const coord2 = coordinates[coordinates.length - 4];
        if (elemChecked.includes('segmento')) {
          if (coord2 !== undefined) {
            coordinates = [(coord1[0] + coord2[0]) / 2,
              (coord1[1] + coord2[1]) / 2,
            ];
          } else {
            coordinates = undefined;
          }
        } else {
          coordinates = coordinates[coordinates.length - 2];
        }
        if (elemChecked.includes('rumbo')) {
          const textAux = this.valueRumboExtra_;
          this.createMeasureTooltipExtra_(coord2, textAux); // coord1
        }
      } else if (this.name === 'MeasureLengthihm') {
        const coord1 = coordinates[coordinates.length - 2];
        const coord2 = coordinates[coordinates.length - 3];
        if (elemChecked.includes('segmento')) {
          if (coord2 !== undefined) {
            coordinates = [(coord1[0] + coord2[0]) / 2,
              (coord1[1] + coord2[1]) / 2,
            ];
          } else {
            coordinates = undefined;
          }
        } else {
          coordinates = coordinates[coordinates.length - 2];
        }
        if (elemChecked.includes('rumbo')) {
          const textAux = this.valueRumboExtra_;
          const coor = this.geomEvent.getCoordinates()[this.geomEvent.getCoordinates().length - 3];
          this.createMeasureTooltipExtra_(coor, textAux);
        }
      }
      // bloque principal pinta la etiqueta donde he hecho click
      if (coordinates !== undefined && (this.tooltipText[1].valor !== this.valueSegmExtra_ || (this.tooltipText[1].valor !== '0 NM' && this.tooltipText[1].valor !== '0 m' && this.tooltipText[1].valor !== '0 km')) && this.valueSegmExtra_ !== undefined) {
        this.createMeasureTooltipExtra_(coordinates, this.valueSegmExtra_);
      }
    }
  }

  /**
   * Handle pointer move.
   *
   * private
   * function
   * @param {ol.MapBrowserEvent} evt - Event pointer move
   */
  pointerMoveHandler_(evt) {
    if (evt.dragging) {
      return;
    }
    let helpMsg = this.helpMsg_;
    if (this.currentFeature_) {
      helpMsg = this.helpMsgContinue_;
    }
    if (!M.utils.isNullOrEmpty(this.helpTooltip_)) {
      this.helpTooltip_.getElement().innerHTML = helpMsg;
      this.helpTooltip_.setPosition(evt.coordinate);
    }
  }

  /**
   * Handle pointer move.
   * private
   * function
   * @param {ol.MapBrowserEvent} evt - Event pointer change
   */
  onGeometryChange_(evt) {
    this.geomEvent = evt.target;
    const plugins = this.facadeMap_.getPlugins().filter((plugin) => {
      return plugin.name === 'measurebarihm';
    });
    const conf = plugins[0].getControls().filter((control) => {
      return (control.name === 'measureconfiguracionihm');
    })[0];
    const elemChecked = conf.getImpl().getChecked();
    const newGeometry = evt.target;
    const coordinates = newGeometry.getCoordinates();
    if (coordinates != null) {
      if (coordinates.length === 1) { // significa que es area
        let oldCoordinates = newGeometry.getCoordinates()[0];
        oldCoordinates = oldCoordinates[newGeometry.getCoordinates()[0].length - 2];
        let previousCoordinates = newGeometry.getCoordinates()[0];
        previousCoordinates = previousCoordinates[newGeometry.getCoordinates()[0].length - 3];
        // let pPreviousCoordinates = newGeometry.getCoordinates()[0];
        // pPreviousCoordinates = pPreviousCoordinates[newGeometry.getCoordinates()[0].length - 4];
        const tooltipText = this.formatGeometry(newGeometry);
        this.tooltipText = tooltipText;
        const tooltipCoord = this.getTooltipCoordinate(newGeometry);
        // diferenciar si tiene dos o tiene tres
        if (!M.utils.isNullOrEmpty(this.measureTooltipOld_) && tooltipText.length < 3) {
          this.measureTooltip_.getElement().innerHTML = tooltipText[1].valor;
          this.measureTooltip_.setPosition(tooltipCoord);
          this.measureTooltipOld_.getElement().innerHTML = tooltipText[0].valor;
          this.measureTooltipOld_.setPosition(oldCoordinates);
        }
        if (!M.utils.isNullOrEmpty(this.measureTooltipOld_) && tooltipText.length === 3) {
          let value = tooltipText[1].valor;
          if (elemChecked.includes('continuo') && elemChecked.includes('rumbo')) {
            value = value.concat(' - '.concat(tooltipText[0].valor));
          }
          this.measureTooltip_.getElement().innerHTML = tooltipText[2].valor;
          this.measureTooltip_.setPosition(tooltipCoord);
          this.measureTooltipOld_.getElement().innerHTML = value;
          this.measureTooltipOld_.setPosition(oldCoordinates);
          if (elemChecked.includes('continuo') && elemChecked.includes('segmento')) {
            this.measureTooltipPrevious_.getElement().innerHTML = tooltipText[0].valor;
            previousCoordinates = [(oldCoordinates[0] + previousCoordinates[0]) / 2,
              (oldCoordinates[1] + previousCoordinates[1]) / 2,
            ];
            this.measureTooltipPrevious_.setPosition(previousCoordinates);
            // left
            const init = this.geomEvent.getCoordinates()[0][0];
            const coordAct =
              newGeometry.getCoordinates()[0][newGeometry.getCoordinates()[0].length - 2];
            this.measureTooltipPprevious_.getElement().innerHTML = tooltipText[0].left;
            this.measureTooltipPprevious_.setPosition([(coordAct[0] + init[0]) / 2,
              (coordAct[1] + init[1]) / 2,
            ]);
          }
          if (!elemChecked.includes('continuo') && elemChecked.includes('segmento')) {
            this.measureTooltipPrevious_.getElement().innerHTML = tooltipText[0].valor;
            previousCoordinates = [(oldCoordinates[0] + previousCoordinates[0]) / 2,
              (oldCoordinates[1] + previousCoordinates[1]) / 2,
            ];
            this.measureTooltipPrevious_.setPosition(previousCoordinates);
          } else {
            this.measureTooltipPrevious_.getElement().innerHTML = tooltipText[0].valor;
            this.measureTooltipPrevious_.setPosition(previousCoordinates);
          }
          if (tooltipText[1].valor !== '0 NM' && tooltipText[1].valor !== '0 m' && tooltipText[1].valor !== '0 km' && tooltipText[1].valor !== '0 ha') {
            setTimeout(() => {
              this.valueSegmExtra_ = tooltipText[1].valor;
              this.valueRumboExtra_ = tooltipText[0].valor;
            }, 900);
          }
        }
        if (!M.utils.isNullOrEmpty(this.measureTooltipOld_) && tooltipText.length === 4) {
          this.measureTooltip_.getElement().innerHTML = tooltipText[3].valor;
          this.measureTooltip_.setPosition(tooltipCoord);
          let value = tooltipText[2].valor;
          if (elemChecked.includes('continuo') && elemChecked.includes('rumbo')) {
            value = value.concat(' - '.concat(tooltipText[0].valor));
          }
          this.measureTooltipOld_.getElement().innerHTML = value;
          this.measureTooltipOld_.setPosition(oldCoordinates);
          if (elemChecked.includes('continuo') && elemChecked.includes('segmento')) {
            // left
            const init = this.geomEvent.getCoordinates()[0][0];
            const coordAct =
              newGeometry.getCoordinates()[0][newGeometry.getCoordinates()[0].length - 2];
            this.measureTooltipLeft_.getElement().innerHTML = tooltipText[1].left;
            this.measureTooltipLeft_.setPosition([(coordAct[0] + init[0]) / 2,
              (coordAct[1] + init[1]) / 2,
            ]);
          }
          let measTool = this.measureTooltipPrevious_;
          const measToolrumbo = this.measureTooltipPrevious_;
          measTool = this.measureTooltipPprevious_;
          const text = tooltipText[1].valor;
          measTool.getElement().innerHTML = text;
          const aux = [(oldCoordinates[0] + previousCoordinates[0]) / 2,
            (oldCoordinates[1] + previousCoordinates[1]) / 2,
          ];
          measTool.setPosition(aux);
          measToolrumbo.getElement().innerHTML = tooltipText[0].valor;
          measToolrumbo.setPosition(previousCoordinates);
          if (tooltipText[1].valor !== '0 NM' && tooltipText[1].valor !== '0 m' && tooltipText[1].valor !== '0 km' &&
            tooltipText[1].valor !== this.valueSegmExtra_ && tooltipText[1].valor !== '0 ha') {
            setTimeout(() => {
              this.valueSegmExtra_ = tooltipText[1].valor;
              this.valueRumboExtra_ = tooltipText[0].valor;
            }, 900);
          }
        }
      } else { // significa que es lenthg
        const tooltipText = this.formatGeometry(newGeometry);
        this.tooltipText = tooltipText;
        const tooltipCoord = this.getTooltipCoordinate(newGeometry);
        this.tooltipCoord = this.getTooltipCoordinate(newGeometry);
        let oldCoordinates = newGeometry.getCoordinates();
        oldCoordinates = oldCoordinates[newGeometry.getCoordinates().length - 2];
        let previousCoordinates = newGeometry.getCoordinates();
        previousCoordinates = previousCoordinates[newGeometry.getCoordinates().length - 2];
        if (!M.utils.isNullOrEmpty(this.measureTooltip_)) {
          if (tooltipText.length === 1) {
            this.measureTooltip_.getElement().innerHTML = tooltipText[0].valor;
            this.measureTooltip_.setPosition(tooltipCoord);
          }
        }
        if (!M.utils.isNullOrEmpty(this.measureTooltipOld_) && tooltipText.length === 2) {
          if (tooltipText[1].valor !== '0 NM' && tooltipText[1].valor !== '0 m' && tooltipText[1].valor !== '0 km' &&
            tooltipText[1].valor !== this.valueSegmExtra_) {
            setTimeout(() => {
              this.valueRumboExtra_ = tooltipText[0].valor;
              this.valueSegmExtra_ = tooltipText[1].valor;
            }, 900);
          }
          this.measureTooltip_.getElement().innerHTML = tooltipText[1].valor;
          this.measureTooltip_.setPosition(tooltipCoord);
          elemChecked.forEach((elem) => {
            if (elem === 'segmento') {
              this.measureTooltipOld_.getElement().innerHTML = tooltipText[0].valor;
              oldCoordinates = [(tooltipCoord[0] + oldCoordinates[0]) / 2,
                (tooltipCoord[1] + oldCoordinates[1]) / 2,
              ];
              this.measureTooltipOld_.setPosition(oldCoordinates);
            } else {
              this.measureTooltipOld_.getElement().innerHTML = tooltipText[0].valor;
              this.measureTooltipOld_.setPosition(oldCoordinates);
            }
          });
        }
        if (!M.utils.isNullOrEmpty(this.measureTooltipOld_) && tooltipText.length === 3) {
          if (tooltipText[1].valor !== '0 NM' && tooltipText[1].valor !== '0 m' && tooltipText[1].valor !== '0 km') {
            setTimeout(() => {
              this.valueRumboExtra_ = tooltipText[0].valor;
              this.valueSegmExtra_ = tooltipText[1].valor;
            }, 900);
          }
          this.measureTooltip_.getElement().innerHTML = tooltipText[2].valor;
          this.measureTooltip_.setPosition(tooltipCoord);
          this.measureTooltipOld_.getElement().innerHTML = tooltipText[1].valor;
          oldCoordinates = [(tooltipCoord[0] + oldCoordinates[0]) / 2,
            (tooltipCoord[1] + oldCoordinates[1]) / 2,
          ];
          this.measureTooltipOld_.setPosition(oldCoordinates);
          this.measureTooltipPrevious_.getElement().innerHTML = tooltipText[0].valor;
          this.measureTooltipPrevious_.setPosition(previousCoordinates);
        }
      }
    } else {
      const tooltipText = this.formatGeometry(newGeometry);
      const tooltipCoord = this.getTooltipCoordinate(newGeometry);
      if (!M.utils.isNullOrEmpty(this.measureTooltip_)) {
        this.measureTooltip_.getElement().innerHTML = tooltipText[0].valor;
        this.measureTooltip_.setPosition(tooltipCoord);
      }
    }
  }

  /**
   * Clear all measures
   *
   * @public
   * @function
   * @api stable
   */
  clear() {
    if (!M.utils.isNullOrEmpty(this.layer_)) {
      this.layer_.getSource().clear();
    }
    this.overlays_.forEach((overlay) => {
      this.facadeMap_.getMapImpl().removeOverlay(overlay);
    }, this);
    this.overlays_.length = 0;
    this.currentFeature_ = null;
  }


  /**
   * This function destroys this control and cleaning the HTML
   *
   * @public
   * @function
   * @api stable
   */
  destroy() {
    this.deactivate();
    this.element.remove();
    this.facadeMap_.removeControls(this);
    this.facadeMap_ = null;
    this.overlays_.length = 0;
  }
}
